/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { switchMap, map, catchError, takeUntil, exhaustMap, tap, mapTo } from 'rxjs/operators';
import { serviceClient } from 'data/api/service/serviceClient';
import { from, of } from 'rxjs';
import {
    CashBoxTasksTabAction,
    CashBoxTasksTabActionType,
    fetchCertUpdatesHistorySuccessAction,
    fetchCertUpdatesHistoryErrorAction,
    requestCertUpdateErrorAction,
    requestCertUpdateSuccessAction,
    fetchCashBoxLogsHistorySuccessAction,
    fetchCashBoxLogsHistoryErrorAction,
    requestCashBoxLogsSuccessAction,
    requestCashBoxLogsErrorAction,
    downloadCashBoxLogsSuccessAction,
    downloadCashBoxLogsErrorAction,
    fetchFirmwareUpdatesHistoryErrorAction,
    requestFirmwareUpdateSuccessAction,
    requestFirmwareUpdateErrorAction,
    fetchSuitableTargetVersionsSuccessAction,
    fetchSuitableTargetVersionsErrorAction,
    fetchFirmwareUpdatesHistorySuccessAction,
    fetchEnvInfoHistorySuccessAction,
    fetchEnvInfoHistoryErrorAction,
    requestEnvInfoSuccessAction,
    requestEnvInfoErrorAction,
} from '../../../actions/CashBoxRegistry/Tabs/cashBoxTasksTabActions';
import { downloadDataAsFile, parseError } from '../../../../utils/utils';
import { i18n } from '../../../translation/i18n';
import { AxiosError } from 'axios';
import { CashBoxTaskType } from '../../../api/service/serviceClient.typings';

export const fetchCertUpdatesHistoryEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_START),
        switchMap((action) => {
            const listName = i18n.t('RegistryScreens.deviceCertUpdatesHistory').toLowerCase();

            return from(
                serviceClient.getCashboxTaskHistory({ ...action.payload, name: CashBoxTaskType.CERTUPDATE }),
            ).pipe(
                map((resp) => {
                    return fetchCertUpdatesHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) => {
                    return of(
                        fetchCertUpdatesHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const requestCertUpdateEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_START),
        switchMap((action) => {
            return from(serviceClient.createCashboxTask(action.payload)).pipe(
                map(() => {
                    return requestCertUpdateSuccessAction();
                }),

                catchError((err: AxiosError) => {
                    if (err.response && err.response.status === 409) {
                        return of(
                            requestCertUpdateErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.certUpdateAlreadyInProgress')),
                            ),
                        );
                    }
                    return of(
                        requestCertUpdateErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.requestToUpdateDeviceCertificateFailed')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const fetchCashBoxLogsHistoryEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_START),
        switchMap((action) => {
            const listName = i18n.t('RegistryScreens.cashBoxLogsHistory').toLowerCase();

            return from(serviceClient.getCashboxTaskHistory({ ...action.payload, name: CashBoxTaskType.LOGS })).pipe(
                map((resp) => {
                    return fetchCashBoxLogsHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) => {
                    return of(
                        fetchCashBoxLogsHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const requestCashBoxLogsEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.REQUEST_LOGS_START),
        switchMap((action) => {
            return from(serviceClient.createCashboxTask(action.payload)).pipe(
                map(() => {
                    return requestCashBoxLogsSuccessAction();
                }),

                catchError((err: AxiosError) => {
                    if (err.response && err.response.status === 409) {
                        return of(
                            requestCashBoxLogsErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.cashBoxLogsRequestAlreadyInProgress')),
                            ),
                        );
                    }
                    return of(
                        requestCashBoxLogsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.requestToCreateCashBoxLogsFailed')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const downloadCashBoxLogsFileEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_START),
        exhaustMap((action) => {
            const fileName = action.payload.fileName;

            return from(serviceClient.downloadCashBoxLogsFile(action.payload.logId)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadCashBoxLogsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadCashBoxLogsErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const fetchFirmwareUpdatesHistoryEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_START),
        switchMap((action) => {
            const listName = i18n.t('RegistryScreens.deviceFirmwareUpdatesHistory').toLowerCase();

            return from(
                serviceClient.getCashboxTaskHistory({ ...action.payload, name: CashBoxTaskType.FWUPDATE }),
            ).pipe(
                map((resp) => {
                    return fetchFirmwareUpdatesHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) => {
                    return of(
                        fetchFirmwareUpdatesHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const requestFirmwareUpdateEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_START),
        switchMap((action) => {
            return from(serviceClient.createCashboxTask(action.payload)).pipe(
                map(() => {
                    return requestFirmwareUpdateSuccessAction();
                }),

                catchError((err: AxiosError) => {
                    if (err.response && err.response.status === 409) {
                        return of(
                            requestFirmwareUpdateErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.firmwareUpdateAlreadyInProgress')),
                            ),
                        );
                    }
                    return of(
                        requestFirmwareUpdateErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.requestToUpdateDeviceFirmwareFailed')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const fetchSuitableFirmwareTargetVersionsEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_START),
        switchMap((action) => {
            return from(serviceClient.getSuitableTargetVersions(action.payload)).pipe(
                map((resp) => {
                    return fetchSuitableTargetVersionsSuccessAction(resp.versions);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchSuitableTargetVersionsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.getSuitableTargetVersionsFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const fetchEnvInfoHistoryEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_START),
        switchMap((action) => {
            const listName = i18n.t('RegistryScreens.envInfoHistory').toLowerCase();

            return from(serviceClient.getCashboxTaskHistory({ ...action.payload, name: CashBoxTaskType.ENVINFO })).pipe(
                map((resp) => {
                    return fetchEnvInfoHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) => {
                    return of(
                        fetchEnvInfoHistoryErrorAction(parseError(err, i18n.t('errors.failedLoadList', { listName }))),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};

export const requestEnvInfoEpic: Epic<CashBoxTasksTabAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxTasksTabActionType.REQUEST_ENV_INFO_START),
        switchMap((action) => {
            return from(serviceClient.createCashboxTask(action.payload)).pipe(
                map(() => {
                    return requestEnvInfoSuccessAction();
                }),

                catchError((err: AxiosError) => {
                    if (err.response && err.response.status === 409) {
                        return of(
                            requestEnvInfoErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.envInfoRequestAlreadyInProgress')),
                            ),
                        );
                    }
                    return of(
                        requestEnvInfoErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.requestToGetEnvInfoFailed')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB))),
            );
        }),
    );
};
